import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../routes/apiRoutes";
import Notiflix from "notiflix";
import { Loader2, Plus, X, IndianRupee } from "lucide-react";

function EnrollmentManagementPage() {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    student: "",
    course: "",
    batch: "",
    fee: "",
    initialPayment: "",
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [studentsRes, coursesRes, batchesRes, enrollmentsRes] =
        await Promise.all([
          axiosInstance.get(apiUrl.GET_ALL_STUDENTS),
          axiosInstance.get(apiUrl.GETCOURSES),
          axiosInstance.get(apiUrl.GET_BATCHES),
          axiosInstance.get(apiUrl.GET_ENROLLMENTS),
        ]);
      setStudents(studentsRes.data?.data || studentsRes.data || []);
      setCourses(coursesRes.data?.data || coursesRes.data || []);
      setBatches(batchesRes.data?.data || batchesRes.data || []);
      setEnrollments(enrollmentsRes.data?.data || []);
    } catch (err) {
      Notiflix.Notify.failure("Failed to load enrollment data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const batchesForCourse = batches.filter(
    (b) => !form.course || b.courseId === form.course || b.courseId?._id === form.course
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.student || !form.course || !form.fee) {
      Notiflix.Notify.failure("Student, course and fee are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        student: form.student,
        course: form.course,
        batch: form.batch || undefined,
        fee: Number(form.fee),
        amountPaid: form.initialPayment
          ? [{ amount: Number(form.initialPayment), mode: "cash" }]
          : [],
      };
      const res = await axiosInstance.post(apiUrl.CREATE_ENROLLMENT, payload);
      if (res.data?.success) {
        Notiflix.Notify.success("Student enrolled successfully");
        setForm({ student: "", course: "", batch: "", fee: "", initialPayment: "" });
        setShowForm(false);
        loadAll();
      }
    } catch (err) {
      Notiflix.Notify.failure(
        err.response?.data?.message || "Failed to create enrollment"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await axiosInstance.patch(apiUrl.TOGGLE_ENROLLMENT(id));
      Notiflix.Notify.success("Status updated");
      loadAll();
    } catch (err) {
      Notiflix.Notify.failure("Failed to update status");
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">
            Enrollment Management
          </h1>
          <p className="text-slate-400 text-sm">
            Assign a student to a course + batch + fee. This is what makes
            their course, lectures, fees and attendance show up in their
            panel.
          </p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-semibold"
        >
          {showForm ? <X size={18} /> : <Plus size={18} />}
          {showForm ? "Cancel" : "New Enrollment"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="bg-[#121826] border border-[#223050] rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Student *
            </label>
            <select
              value={form.student}
              onChange={(e) => setForm({ ...form, student: e.target.value })}
              className="w-full rounded-lg border border-[#1d3350] bg-[#0d1220] text-slate-200 px-3 py-2 text-sm"
              required
            >
              <option value="">Select student</option>
              {students.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name} ({s.studentId || s.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Course *
            </label>
            <select
              value={form.course}
              onChange={(e) =>
                setForm({ ...form, course: e.target.value, batch: "" })
              }
              className="w-full rounded-lg border border-[#1d3350] bg-[#0d1220] text-slate-200 px-3 py-2 text-sm"
              required
            >
              <option value="">Select course</option>
              {courses.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.courseName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Batch{" "}
              <span className="text-slate-500">
                (needed for attendance + WhatsApp group)
              </span>
            </label>
            <select
              value={form.batch}
              onChange={(e) => setForm({ ...form, batch: e.target.value })}
              className="w-full rounded-lg border border-[#1d3350] bg-[#0d1220] text-slate-200 px-3 py-2 text-sm"
            >
              <option value="">No batch yet</option>
              {batchesForCourse.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.batchName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Total Fee (₹) *
            </label>
            <input
              type="number"
              min="0"
              value={form.fee}
              onChange={(e) => setForm({ ...form, fee: e.target.value })}
              className="w-full rounded-lg border border-[#1d3350] bg-[#0d1220] text-slate-200 px-3 py-2 text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Initial Payment Received (₹)
            </label>
            <input
              type="number"
              min="0"
              value={form.initialPayment}
              onChange={(e) =>
                setForm({ ...form, initialPayment: e.target.value })
              }
              placeholder="0"
              className="w-full rounded-lg border border-[#1d3350] bg-[#0d1220] text-slate-200 px-3 py-2 text-sm"
            />
          </div>

          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 disabled:opacity-60 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-semibold"
            >
              {saving ? "Saving..." : "Create Enrollment"}
            </button>
          </div>
        </form>
      )}

      <div className="bg-[#121826] border border-[#223050] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center">
            <Loader2 className="animate-spin text-slate-400" />
          </div>
        ) : enrollments.length === 0 ? (
          <div className="p-10 text-center text-slate-400">
            No enrollments yet. Click "New Enrollment" to assign a student to
            a course.
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="text-slate-400 text-xs border-b border-[#223050]">
                <th className="p-3">Student</th>
                <th className="p-3">Course</th>
                <th className="p-3">Batch</th>
                <th className="p-3">Fee</th>
                <th className="p-3">Paid</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((e) => {
                const paid = (e.amountPaid || []).reduce(
                  (sum, p) => sum + p.amount,
                  0
                );
                return (
                  <tr key={e._id} className="border-b border-[#223050]/50">
                    <td className="p-3 text-slate-200">{e.student?.name}</td>
                    <td className="p-3 text-slate-300 capitalize">
                      {e.course?.courseName}
                    </td>
                    <td className="p-3 text-slate-300">
                      {e.batch?.batchName || (
                        <span className="text-red-400 text-xs">No batch</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-300 flex items-center gap-0.5">
                      <IndianRupee size={12} /> {e.fee}
                    </td>
                    <td className="p-3 text-slate-300 flex items-center gap-0.5">
                      <IndianRupee size={12} /> {paid}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => handleToggle(e._id)}
                        className={`text-xs px-3 py-1 rounded-full font-semibold ${
                          e.isActive
                            ? "bg-green-500/10 text-green-400"
                            : "bg-red-500/10 text-red-400"
                        }`}
                      >
                        {e.isActive ? "Active" : "Inactive"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default EnrollmentManagementPage;
